import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import AdminHeader from '../components/admin/AdminHeader';
import AdminSidebar from '../components/admin/AdminSidebar';

export default function AdminDashboard({ navigate, user, onSignOut }) {
  const [activeView, setActiveView] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterFormat, setFilterFormat] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'inquiries'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const docs = [];
      querySnapshot.forEach((doc) => {
        docs.push({ id: doc.id, ...doc.data() });
      });
      setInquiries(docs);
      setError(null);
    } catch (err) {
      console.error("Failed to load inquiries:", err);
      setError("Error fetching inquiries. Please check your Firestore security rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeView === 'inquiries') {
      fetchInquiries();
    }
  }, [activeView]);

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter & Search logic
  const filteredInquiries = inquiries.filter((inq) => {
    const formatValue = inq.format || inq.delivery || 'Onsite';
    const matchesFormat = filterFormat === 'All' || formatValue.toLowerCase() === filterFormat.toLowerCase();
    
    const nameVal = inq.name || '';
    const emailVal = inq.email || '';
    const orgVal = inq.organization || inq.organisation || '';
    const reqVal = inq.requirement || '';
    
    const matchesSearch = 
      nameVal.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emailVal.toLowerCase().includes(searchTerm.toLowerCase()) ||
      orgVal.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reqVal.toLowerCase().includes(searchTerm.toLowerCase());
      
    return matchesFormat && matchesSearch;
  });

  // Calculate metrics
  const totalInquiries = inquiries.length;
  const onsiteCount = inquiries.filter(i => (i.format || i.delivery || '').toLowerCase() === 'onsite').length;
  const onlineCount = inquiries.filter(i => (i.format || i.delivery || '').toLowerCase() === 'online').length;
  const hybridCount = inquiries.filter(i => (i.format || i.delivery || '').toLowerCase() === 'hybrid').length;

  const renderInquiriesView = () => (
    <>
      <div className="admin-header">
        <div>
          <span className="eyebrow">Management Portal</span>
          <h1>Inquiry Dashboard</h1>
        </div>
        <button 
          className="btn btn-secondary" 
          onClick={fetchInquiries} 
          disabled={loading}
          style={{ minHeight: '40px', padding: '0 16px', borderRadius: '8px' }}
        >
          🔄 Refresh Data
        </button>
      </div>

      {/* Metrics Overview Cards */}
      <div className="admin-metrics-grid">
        <div className="admin-metric-card">
          <span>Total Inquiries</span>
          <strong>{totalInquiries}</strong>
        </div>
        <div className="admin-metric-card">
          <span>Onsite Requests</span>
          <strong style={{ color: '#0b8f55' }}>{onsiteCount}</strong>
        </div>
        <div className="admin-metric-card">
          <span>Online Requests</span>
          <strong style={{ color: '#2970a6' }}>{onlineCount}</strong>
        </div>
        <div className="admin-metric-card">
          <span>Hybrid / Other</span>
          <strong style={{ color: '#a67029' }}>{hybridCount}</strong>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="admin-controls">
        <div className="admin-search-wrapper">
          <input 
            type="text" 
            className="admin-search-input" 
            placeholder="Search by name, organization, email or keyword..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="admin-filter-group">
          {['All', 'Onsite', 'Online', 'Hybrid'].map((format) => (
            <button
              key={format}
              className={`admin-filter-btn ${filterFormat === format ? 'active' : ''}`}
              onClick={() => setFilterFormat(format)}
            >
              {format}
            </button>
          ))}
        </div>
      </div>

      {/* Error Notice */}
      {error && (
        <div style={{
          background: '#fef1f1',
          color: '#d92d20',
          padding: '16px 20px',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #fda29b',
          fontWeight: 700
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Inquiries Table */}
      <div className="admin-table-wrapper">
        <div className="admin-table-header">
          <div>Submitted On</div>
          <div>Contact Info</div>
          <div>Organization</div>
          <div>Format</div>
          <div>Requirement Description</div>
        </div>
        
        {loading ? (
          <div style={{ padding: '60px 0', textAlignment: 'center', display: 'flex', justifyContent: 'center' }}>
            <span className="spinner" style={{
              width: '32px',
              height: '32px',
              border: '3px solid rgba(11, 143, 85, 0.2)',
              borderTopColor: '#0b8f55',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}></span>
          </div>
        ) : filteredInquiries.length === 0 ? (
          <div className="admin-empty-state">
            <h3>No inquiries found</h3>
            <p>Try resetting filters or changing your search criteria.</p>
          </div>
        ) : (
          filteredInquiries.map((inq) => {
            const formatValue = inq.format || inq.delivery || 'Onsite';
            const badgeClass = formatValue.toLowerCase().replace(/\s+/g, '-');
            
            return (
              <div key={inq.id} className="admin-table-row">
                <div style={{ fontWeight: 800 }}>
                  {formatDate(inq.createdAt)}
                </div>
                <div>
                  <strong style={{ color: '#0e2118', display: 'block' }}>{inq.name}</strong>
                  <span style={{ fontSize: '0.78rem', color: '#607067', display: 'block' }}>{inq.email}</span>
                  <span style={{ fontSize: '0.78rem', color: '#607067', display: 'block' }}>{inq.phone}</span>
                </div>
                <div>
                  <strong style={{ color: '#2c3f34' }}>{inq.organization || inq.organisation || 'N/A'}</strong>
                  <span style={{ fontSize: '0.78rem', color: '#607067', display: 'block' }}>
                    {inq.participants ? `${inq.participants} participants` : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className={`admin-format-badge ${badgeClass}`}>
                    {formatValue}
                  </span>
                </div>
                <div style={{ color: '#47574f', fontSize: '0.85rem', lineHeight: '1.45', whiteSpace: 'pre-wrap' }}>
                  {inq.requirement}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  const renderUsersView = () => (
    <>
      <div className="admin-header">
        <div>
          <span className="eyebrow">Management Portal</span>
          <h1>User Accounts</h1>
        </div>
      </div>
      <div className="admin-table-wrapper" style={{ padding: '40px', textAlign: 'center', color: '#607067' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>👥</div>
        <h3 style={{ color: '#0e2118', marginBottom: '12px', fontSize: '1.25rem', fontWeight: 800 }}>
          User Registration & Identity Management
        </h3>
        <p style={{ maxWidth: '520px', margin: '0 auto 24px', fontSize: '0.9rem', lineHeight: '1.6' }}>
          User profiles, signups, passwords, and verification flags are managed securely directly inside your Google Firebase console to guarantee absolute security.
        </p>
        <a 
          href="https://console.firebase.google.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="btn btn-primary"
          style={{ display: 'inline-flex', minHeight: '44px', padding: '0 24px', borderRadius: '8px' }}
        >
          Open Firebase Console
        </a>
      </div>
    </>
  );

  const renderSettingsView = () => (
    <>
      <div className="admin-header">
        <div>
          <span className="eyebrow">Management Portal</span>
          <h1>Console Settings</h1>
        </div>
      </div>
      <div className="admin-table-wrapper" style={{ padding: '40px' }}>
        <h3 style={{ color: '#0e2118', marginBottom: '24px', fontSize: '1.2rem', fontWeight: 800 }}>
          System Configuration
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '600px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '12px', borderBottom: '1px solid #f0f4f1' }}>
            <strong>Firebase Project ID</strong>
            <span style={{ fontFamily: 'monospace', color: '#607067' }}>mr-excel-sri-lanka-web</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '12px', borderBottom: '1px solid #f0f4f1' }}>
            <strong>EmailJS Services Status</strong>
            <span style={{ color: '#0b8f55', fontWeight: '800' }}>● Connected</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '12px', borderBottom: '1px solid #f0f4f1' }}>
            <strong>Bypass Verification User</strong>
            <span style={{ fontFamily: 'monospace', color: '#607067' }}>admin@mrexcelsrilanka.com</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '12px' }}>
            <strong>Production Build Version</strong>
            <span style={{ color: '#607067' }}>v1.1.2</span>
          </div>
        </div>
      </div>
    </>
  );

  const renderView = () => {
    switch (activeView) {
      case 'inquiries':
        return renderInquiriesView();
      case 'users':
        return renderUsersView();
      case 'settings':
        return renderSettingsView();
      default:
        return renderInquiriesView();
    }
  };

  return (
    <main className="admin-dashboard-page">
      <AdminHeader user={user} onSignOut={onSignOut} navigate={navigate} />
      
      <div className="admin-layout-body">
        <AdminSidebar activeView={activeView} setActiveView={setActiveView} />
        <div className="admin-layout-content">
          {renderView()}
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}} />
    </main>
  );
}
